import callApi, { CallApiResult } from 'src/api';

/** R31-4 translator surface of the templating service (Mein Bereich › Übersetzungen). */
const BASE = '/template/v3/translator';

export interface TranslatorTag {
    id: number;
    name: string;
}

export interface TranslatorRights {
    editable: string[];
    viewable: string[];
    /** R34-3: the text-block tags the account may see; empty = every block. */
    tags: TranslatorTag[];
}

export interface TranslatorTranslation {
    languageIso2: string;
    value: string | null;
    editable: boolean;
}

export interface TranslatorTextBlock {
    key: string;
    description: string | null;
    type: string;
    translations: TranslatorTranslation[];
    tags: TranslatorTag[];
    /** R49-4: the text in the requested `valueLanguage` (fallback en → de → default language). */
    text?: string | null;
}

/** R49-4 "Suchen in" — the scopes of IT-Abteilung › Marketing-Textblöcke. */
export type TranslatorSearchScope = 'KEY_AND_TEXT' | 'KEY' | 'TEXT';

export interface TranslatorTextBlockQuery {
    search: string;
    searchIn: TranslatorSearchScope;
    page: number;
    pageSize: number;
    tag?: number | null;
    /** The website language the list text is shown in. */
    valueLanguage: string;
}

export interface TranslatorPage<T> {
    items: T[];
    total: number;
    page: number;
    pageSize: number;
}

export interface TranslatorDocument {
    key: string;
    displayName: string | null;
    note: string | null;
    type: string;
}

export interface TranslatorDocumentDetail extends TranslatorDocument {
    blocks: string[];
}

export const getTranslatorRights = (): Promise<CallApiResult<TranslatorRights>> => callApi(`${BASE}/rights`);

export const listTranslatorTextBlocks = (
    query: TranslatorTextBlockQuery
): Promise<CallApiResult<TranslatorPage<TranslatorTextBlock>>> => {
    const params: Record<string, string | number> = {
        page: query.page,
        pageSize: query.pageSize,
        valueLanguage: query.valueLanguage,
    };
    if (query.search) {
        params.search = query.search;
        params.searchIn = query.searchIn;
    }
    if (query.tag) params.tag = query.tag;
    return callApi(`${BASE}/languageblocks`, { params });
};

export const getTranslatorTextBlock = (key: string): Promise<CallApiResult<TranslatorTextBlock>> =>
    callApi(`${BASE}/languageblocks/${encodeURIComponent(key)}`);

/** R49-4: one "Speichern" writes every changed editable language — all or none. */
/**
 * Nachtrag F3: ordering caches resolved texts for 5 minutes — after a save the shop drops the
 * key's cached values (every language/country) so the text shows at once.
 */
export const evictOrderingTextCache = (key: string): Promise<CallApiResult<void>> =>
    callApi(`/api/templateCache/${encodeURIComponent(key)}`, { method: 'DELETE' });

export const putTranslatorTranslations = (
    key: string,
    valuesByLanguage: Record<string, string>
): Promise<CallApiResult<void>> =>
    callApi(`${BASE}/languageblocks/${encodeURIComponent(key)}/translations`, {
        method: 'PUT',
        data: valuesByLanguage,
    });

export const listTranslatorDocuments = (
    search: string,
    page: number,
    pageSize: number
): Promise<CallApiResult<TranslatorPage<TranslatorDocument>>> =>
    callApi(`${BASE}/documents`, { params: search ? { search, page, pageSize } : { page, pageSize } });

export const getTranslatorDocument = (key: string): Promise<CallApiResult<TranslatorDocumentDetail>> =>
    callApi(`${BASE}/documents/${encodeURIComponent(key)}`);
